import type { ValidationRuleCollection } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';

import type { IBatteryInputObject, IDynamicCycleSeries } from '@/interfaces/storageForecasting/IBatteryInputData.ts';
import type { IIdName } from '@/interfaces/general/IIdName.ts';
import type { TStorageForecastCountries } from '@/types/TStorageForecastCountries.ts';
import { cmContractDurations } from '@/utils/storageForecasting/assetValueOptions.ts';

export const months: Array<string> = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
];

export const monthsInYear = 12;
export const hoursInDay = 24;

export function sumOfValuesInAnyMonthIsLessThanPoint6 (arrayToCheck: Array<Array<number>>): string | null {
    return arrayToCheck.some(month => month.reduce((acc, curr) => acc + curr, 0) < 0.6) ? 'Must have an average availability of over 2.5% in every month' : null;
}

export function availabilityArrayIsMalformed (arrayToCheck: Array<Array<number>>): string | null {
    if (arrayToCheck.length !== monthsInYear) {
        return 'Array must have 12 months (columns).';
    }

    for (let m = 0; m < arrayToCheck.length; m += 1) {
        const month = arrayToCheck[m];

        if (!Array.isArray(month)) {
            return `${months[m]} is not an array.`;
        }
        if (month.length !== hoursInDay) {
            return `${months[m]} must have 24 hourly values.`;
        }

        for (let h = 0; h < month.length; h += 1) {
            const val = month[h];
            if (typeof val !== 'number' || Number.isNaN(val)) {
                return `Invalid number at ${months[m]}, hour ${String(h).padStart(2, '0')}:00`;
            }
            if (val < 0 || val > 1) {
                return `Value out of range (0–1) at ${months[m]}, hour ${String(h).padStart(2, '0')}:00`;
            }
        }
    }
    return null;
}

export function availabilityArrayHasError (arrayToCheck: Array<Array<number>>): string | null {
    const malformedError = availabilityArrayIsMalformed(arrayToCheck);
    if (malformedError) return malformedError;

    const averageAvailabilityWarning = sumOfValuesInAnyMonthIsLessThanPoint6(arrayToCheck);
    if (averageAvailabilityWarning) return averageAvailabilityWarning;

    return null;
}

export function spreadYearlyValuesToMonthly (yearly: Array<number>, yearsCount: number): Array<number> {
    const expanded: Array<number> = [];
    for (let y = 0; y < yearsCount; y += 1) {
        const yearlyValue = yearly[y] ?? 0;
        for (let m = 0; m < 12; m += 1) {
            expanded.push(yearlyValue);
        }
    }
    return expanded;
}

export function contractMonthlyValuesToYearly (monthly: Array<number>): Array<number> {
    const compressed: Array<number> = [];
    for (let y = 0; y < monthly.length; y += 12) {
        compressed.push(monthly[y] ?? 0);
    }
    return compressed;
}

export const greaterThanZeroErrorMessage = 'Value must be greater than 0';
export const greaterThanZeroErrorMessageInclusive = 'Value must be greater than or equal to 0';
export const startDateFirstOfMonthErrorMessage: string = 'Start date must be on the first of the month.';
export const cmStartsAferRunFinishesErrorMessage = 'CM contract must start before the end of the simulation.';
export const cmEndBeforeRunStartsErrorMessage = 'CM contract must not finish before battery energises.';

export function filterGridConnectionOptions (
    options: Array<IIdName>,
    country: TStorageForecastCountries,
    tlfZoneId: string | null | undefined,
): Array<IIdName> {
    if (country === 'Germany') {
        return options.filter((option) => option.id === 'Transmission');
    }
    if (country === 'GB' && tlfZoneId?.toLowerCase().includes('scotland')) {
        return options.filter((option) => option.id !== 'EHV132');
    }
    return options;
}

export function minDateErrorMessage (minDate: string): string {
    return `Date must be after ${minDate}`;
}

export function maxDateErrorMessage (maxDate: string): string {
    return `Date must be before ${maxDate}`;
}

export function lessThanNumberErrorMessage (max: number): string {
    return `Value must be less than ${max}`;
}

export function greaterThanZero (value: number): boolean {
    return value > 0;
}

export function greaterThanOrEqualToZero (value: number): boolean {
    return value >= 0;
}

/** CfD policy type is valid when CfD is off, country has a single type, or value is not None. */
export function cfdTypeValueIsEitherNotRequiredOrNotNone (
    value: string,
    hasCfd: boolean,
    countryHasMultiplePolicySupportTypes: boolean,
): boolean {
    if (!hasCfd) return true;
    if (!countryHasMultiplePolicySupportTypes) return true;
    return value !== 'None';
}

export function selectionIsInOptionsWhenRequired (
    value: string | null | undefined,
    options: Array<IIdName>,
    checkRequired: boolean,
): boolean {
    if (!checkRequired) return true;
    if (!value) return false;
    return options.some(option => option.id === value);
}

export function createNameValidator (): (value: string) => boolean {
    return (value: string): boolean => /^[a-zA-Z0-9-_ ]*$/.test(value);
}

export function requiredAndGreaterThanMinDate (minDate: string, checkRequired: boolean): (value: string) => boolean {
    return (value: string): boolean => {
        if (checkRequired !== true) return true;
        const date = new Date(value);
        const min = new Date(minDate);
        return date >= min;
    };
}

export function requiredAndLessThanOrEqualToMaxDate (maxDate: string, checkRequired: boolean): (value: string) => boolean {
    return (value: string): boolean => {
        if (checkRequired !== true) return true;
        const date = new Date(value);
        const max = new Date(maxDate);
        return date <= max;
    };
}

export function requiredAndStartDateIsFirstOfMonth (checkRequired: boolean): (value: string) => boolean {
    return (value: string): boolean => {
        if (!checkRequired) return true;
        if (!value) return false;

        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return false;
        return date.getDate() === 1;
    };
}

export function requiredAndCmStartBeforeEndDate (getEndDate: () => string | null, checkRequired: boolean): (value: string) => boolean {
    return (value: string): boolean => {
        if (!checkRequired) return true;
        if (!value) return false;

        const cmContractStart = new Date(value);
        const endStr = getEndDate();
        if (!endStr) return true;

        const runEnd = new Date(endStr);
        return cmContractStart < runEnd;
    };
}

export function addYears (date: Date, years: number): Date {
    const newDate = new Date(date);
    newDate.setFullYear(newDate.getFullYear() + years);
    return newDate;
}

export function requiredAndCmContractEndsAfterModelStart (
    getModelStartDate: () => string | null,
    cmType: string,
    checkRequired: boolean,
): (value: string) => boolean {
    return (value: string): boolean => {
        if (!checkRequired) return true;
        if (!value) return false;

        if (cmType === 'None') return true;

        const cmContractStart = new Date(value);
        const octoberFirstStartYear = new Date(cmContractStart.getFullYear(), 9, 1);
        let endDateForFirstYearOfContract: Date;
        if (cmContractStart >= octoberFirstStartYear) {
            endDateForFirstYearOfContract = new Date(octoberFirstStartYear.getFullYear() + 1, 8, 30);
        } else {
            endDateForFirstYearOfContract = new Date(cmContractStart.getFullYear(), 8, 30);
        }
        const modelStartStr = getModelStartDate();
        if (!modelStartStr) return true;

        const cmItem = cmContractDurations.find(item => item.id === cmType);
        const cmLength = Number(cmItem?.name);
        if (!cmItem || Number.isNaN(cmLength)) return true;

        const modelStart = new Date(modelStartStr);
        const contractEnd = addYears(endDateForFirstYearOfContract, cmLength - 1);

        return contractEnd >= modelStart;
    };
}

export function lessThanOrEqualTo (max: number): (value: number) => boolean {
    return (value: number): boolean => value <= max;
}

export function isNullOrGreaterThanZero (value: number | string | null): boolean {
    return value === null || value === '' || Number(value) > 0;
}

export function validRefurbishmentAge (
    refurbishAge: number | string | null,
    startDateIso: string,
    endDateInclusiveIso: string,
): boolean {
    if (refurbishAge == null) return true;
    const startYear = new Date(startDateIso).getFullYear();
    const endYear = new Date(endDateInclusiveIso).getFullYear();
    const yearsRun = endYear - startYear + 1;
    return yearsRun >= Number(refurbishAge);
}

export function siteHasRampRestrictionsIsValid (showRampRates: boolean, isStandAlone: boolean, value: boolean): boolean {
    if (!showRampRates) return true;
    if (isStandAlone) return true;
    return !value;
}

export function siteHasFrCapacityRestrictionsIsValid (showFcaRestrictions: boolean, standAlone: boolean, value: boolean): boolean {
    if (!showFcaRestrictions) return true;
    if (standAlone) return true;
    return !value;
}

export interface IDegradationValueIndexRuleOptions {
    max: number;
    zeroInclusive: boolean | (() => boolean);
    /** When true, bounds checks are skipped (cell validators always pass). */
    skipCellValidation?: () => boolean;
}

/**
 * Vuelidate rules for each numeric index of a degradation `values` array.
 * Validator names must include `Modal` so DegradationWrapper can tie errors to cells via silent errors.
 */
export function buildDegradationValuesIndexRules (
    getValues: () => number[],
    options: IDegradationValueIndexRuleOptions,
): Record<string, ValidationRuleCollection<number>> {
    const values = getValues();
    const safeValues = Array.isArray(values) ? values : [];
    const {
        max,
        zeroInclusive,
        skipCellValidation = (): boolean => false,
    } = options;

    const isZeroInclusive = (): boolean => (
        typeof zeroInclusive === 'function' ? zeroInclusive() : zeroInclusive
    );

    const rules: Record<string, ValidationRuleCollection<number>> = {};

    safeValues.forEach((_, index) => {
        const key = String(index);
        const indexRules: ValidationRuleCollection<number> = {
            cellMinModal: helpers.withMessage(
                (): string => (isZeroInclusive() ? greaterThanZeroErrorMessageInclusive : greaterThanZeroErrorMessage),
                (v: number): boolean => skipCellValidation() || (isZeroInclusive() ? v >= 0 : v > 0),
            ),
            cellMaxModal: helpers.withMessage(
                lessThanNumberErrorMessage(max),
                (v: number): boolean => skipCellValidation() || v <= max,
            ),
        };
        rules[key] = indexRules;
    });

    return rules;
}

export function isAvailabilityValidCheck (nullableErrorMessage: string | null): boolean {
    return nullableErrorMessage === null;
}

/** True when both granularities match (including both null). */
export function onsiteWindSolarGranularitiesMatch (
    valuesAndGranularity1: IBatteryInputObject | null,
    valuesAndGranularity2: IBatteryInputObject | null,
): boolean {
    if (valuesAndGranularity1 === null && valuesAndGranularity2 === null) return true;
    return valuesAndGranularity1?.granularity === valuesAndGranularity2?.granularity;
}

export function areThereNoRampUpOrDownRestrictions (rampUpFirst: number, rampDownFirst: number): boolean {
    return rampUpFirst === 100 && rampDownFirst === 100;
}

/**
 * Returns true when cycle counts are unique and between 0 and 10 (inclusive).
 * Requires at least one entry.
 */
export function isCycleCountsAscending (cycleSeries: Array<IDynamicCycleSeries> | undefined): boolean {
    if (!cycleSeries?.length) return false;
    const seenCycleCounts = new Set<number>();

    for (let i = 0; i < cycleSeries.length; i += 1) {
        const cycleCount = cycleSeries[i].numberOfCycles;
        if (cycleCount < 0 || cycleCount > 10) return false;
        if (seenCycleCounts.has(cycleCount)) return false;
        seenCycleCounts.add(cycleCount);
    }
    return true;
}

/**
 * Returns true when for every time period index, values are non-increasing
 * as cycle count increases (higher cycles <= lower cycles at same period).
 * Input order does not matter.
 */
export function isCycleSeriesMonotonic (cycleSeries: Array<IDynamicCycleSeries> | undefined): boolean {
    if (!cycleSeries?.length) return true;

    const sortedSeries = [...cycleSeries].sort((a, b) => a.numberOfCycles - b.numberOfCycles);

    for (let i = 1; i < sortedSeries.length; i += 1) {
        const curr = sortedSeries[i].values;
        const prev = sortedSeries[i - 1].values;
        for (let j = 0; j < curr.length; j += 1) {
            if (curr[j] !== undefined && prev[j] !== undefined && curr[j] > prev[j]) return false;
        }
    }
    return true;
}
